//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import remove from 'assets/remove.svg';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { BuildVersionList, toNumber } from 'utils/helper';
import { getItem } from 'utils/localStorageHelper';
import { sseConnectionManager } from 'utils/sseConnectionManager';
import { useConfig } from './ConfigProvider';

interface ToastContextType {
  addToast: (params: ToastType) => void;
  startSse: () => void;
  closeSse: () => void;
}
interface ToastsProviderType {
  children: React.ReactNode;
}

interface SseToastData {
  i18nToastSseKey?: string;
  message?: string;
  toastColor?: string;
  siteId?: string | number;
  [key: string]: unknown;
}

export interface ToastType {
  icon?: string;
  title?: string;
  message: string | React.ReactNode;
  duration?: number; // defultValue = 3000
  color?:
    | ''
    | 'bg-green-200'
    | 'bg-rose-200'
    | 'bg-toast-error-50'
    | 'bg-toast-info-50'
    | 'bg-toast-success-50'
    | 'bg-toast-warning-50'
    | 'bg-toast-primary-200'
    | 'bg-cyfi-error'
    | 'bg-cyfi-info'
    | 'bg-cyfi-success'
    | 'bg-cyfi-warning'
    | 'bg-cyfi-primary'; // defultValue = ''
  text?: 'text-toast-error-800' | 'text-toast-info-800' | 'text-toast-success-800' | 'text-toast-warning-800' | 'text-cyfi-btn';
  svg?: 'toastIcon-warning' | 'toastIcon-success' | 'toastIcon-error' | 'toastIcon-info' | 'cyfi-svg';
  closeFn?: VoidFunction;
  svgCssType?: string;
  toastCssType?: string;
  textCssType?: string;
}

export const Toast = ({
  title,
  message,
  duration,
  color,
  icon,
  text,
  svg,
  closeFn,
  svgCssType = 'w-5 h-5 mr-4',
  toastCssType = 'flex w-full max-w-md p-2 text-gray-500 rounded shadow mb-4',
  textCssType = 'text-xs font-normal',
}: ToastType) => {
  const [show, setShow] = useState<boolean>(true);
  const timeout = useRef<NodeJS.Timeout>();

  const close = useCallback(() => {
    clearTimeout(timeout.current);
    setShow(false);
    if (closeFn) closeFn();
  }, [closeFn]);

  useEffect(() => {
    if (show && duration) {
      timeout.current = setTimeout(() => setShow(false), duration);
    }
    return () => clearTimeout(timeout.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, duration]);

  if (!show) return null;

  return (
    <>
      <div className={`${toastCssType} ${color}`}>
        <div className={`${duration ? 'mr-2' : ''} ${message === '' ? 'pt-1.5' : 'pt-1'} flex items-start`}>
          {icon && (
            <img className={`${svgCssType} ${svg}`} src={icon} alt="" onError={(e) => ((e.target as HTMLTextAreaElement).style.display = 'none')} />
          )}
          <div className={`flex-col items-start flex justify-center ${text}`}>
            {title && <p className="text-sm font-semibold whitespace-pre-wrap">{title}</p>}
            <p className={`${textCssType} whitespace-pre-wrap`}>{message}</p>
          </div>
        </div>
        <button type="button" className="btn btn-ghost ml-auto hover:bg-transparent" onClick={close}>
          <img src={remove} className={`w-4 h-4 ${svg}`} alt="close" />
        </button>
      </div>
    </>
  );
};

const ToastContext = React.createContext<ToastContextType>(null!);

export const ToastProvider = ({ children }: ToastsProviderType) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [toasts, setToasts] = useState<ToastType[]>([]);
  const { cbtApiUrl, buildVersion } = useConfig();

  const pathnameRef = useRef(location.pathname);
  const unsubscribeRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    pathnameRef.current = location.pathname;
  }, [location.pathname]);

  const addToast = useCallback(({ title, message, duration = 3000, color = '', icon, text, svg }: ToastType) => {
    setToasts((prevToasts) => [...prevToasts, { title, message, duration, color, icon, text, svg }]);
  }, []);

  const processSseData = useCallback(
    (data: SseToastData | string) => {
      if (document.visibilityState !== 'visible') return;
      if (/^(\/sites\/\d+)?\/rtty\//.test(location.pathname)) return;
      const parsedData = typeof data === 'string' ? JSON.parse(data) : data;

      let { i18nToastSseKey, message, toastColor, siteId, ...params } = parsedData;

      if (
        buildVersion !== BuildVersionList.portal ||
        (buildVersion === BuildVersionList.portal && toNumber(siteId) === toNumber(getItem('siteId')))
      ) {
        const toastProps: ToastType = { message, color: toastColor ?? 'bg-cyfi-info' };

        if (i18nToastSseKey) {
          const translationKey = `toast.sse.${i18nToastSseKey}`;
          if (i18n.exists(translationKey)) {
            toastProps.message = t(translationKey, params) as React.ReactNode;
          }
          if (i18nToastSseKey === 'rontoServiceUpgradeTimeout') {
            toastProps.duration = 10000;
          }
        }

        if (message || i18nToastSseKey) addToast(toastProps);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [buildVersion, t, i18n]
  );

  const startSse = useCallback(() => {
    if (!cbtApiUrl) return;
    if (unsubscribeRef.current) return; // 已經訂閱中
    const baseUrl = cbtApiUrl.replace(/\/$/, '');
    const sseUrl = `${baseUrl}/api/sse`;
    unsubscribeRef.current = sseConnectionManager.subscribe('SSE', sseUrl, processSseData);
  }, [cbtApiUrl, processSseData]);

  const closeSse = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
  }, []);

  useEffect(() => {
    startSse();
    return () => closeSse();
  }, [startSse, closeSse]);

  const toastContextValue = useMemo(
    () => ({
      addToast,
      startSse,
      closeSse,
    }),
    [addToast, startSse, closeSse]
  );

  return (
    <ToastContext.Provider value={toastContextValue}>
      {children}
      {/* toast z-index 需要比 modal z-index:999 高 */}
      <div className="toast toast-center flex items-center w-full max-w-xs" style={{ maxWidth: '33vw', zIndex: 1000 }}>
        {toasts.map((toast, index) => {
          return (
            <Toast
              key={index}
              duration={toast.duration}
              color={toast.color}
              title={toast.title}
              message={toast.message}
              icon={toast.icon}
              text={toast.text}
              svg={toast.svg}
            ></Toast>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = () => React.useContext(ToastContext);
